/**
 * useFeature Hook - React hook for checking subscription features
 * SAFE: Does not make API calls until user is authenticated
 */

import {
  useState,
  useEffect,
  useMemo,
  createContext,
  useContext,
  useCallback,
  ReactNode,
} from 'react';
import { useAppSelector } from '../redux/stateHooks';
import { hasPrivilege } from '../utility/hasPrivilege';
import { APP_FEATURES } from '../constants';

const SUBSCRIPTION_STORAGE_KEY = 'classveew_subscription';

export const FEATURES = {
  CAN_MANAGE_STUDENTS: 'can_manage_students',
  CAN_MANAGE_TEACHERS: 'can_manage_teachers',
  CAN_MANAGE_ROLES: 'can_manage_roles',
  HAS_STUDENT_ATTENDANCE: 'has_student_attendance',
  STUDENT_ATTENDANCE_CHECKIN: 'student_attendance_checkin',
  HAS_TEACHER_ATTENDANCE: 'has_teacher_attendance',
  ANNOUNCEMENTS: 'announcements',
  EVENT_SURVEY: 'event_survey',
  PAID_EVENTS: 'paid_events',
  AWARD_ISSUE_LEVEL: 'award_issue_level',
  LIBRARY_MANAGEMENT: 'library_management',
  LIBRARY_BOOK_LIMIT: 'library_book_limit',
  LIBRARY_CIRCULATION: 'library_circulation',
  EXAM_MANAGEMENT: 'exam_management',
  CAN_MANAGE_ACADEMICS: 'can_manage_academics',
  CAN_MANAGE_TIMETABLE: 'can_manage_timetable',
  FEE_MANAGEMENT: 'fee_management',
  ONLINE_PAYMENT: 'online_payment',
  ACADEMIC_YEAR: 'academic_year',
  OPERATIONAL_TIME: 'operational_time',
} as const;

export type FeatureCode = (typeof FEATURES)[keyof typeof FEATURES];

interface SubscriptionContextType {
  subscription: any | null;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
  hasFeature: (featureCode: string) => boolean;
  getFeatureValue: (featureCode: string) => string | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

const getCachedSubscription = (): any | null => {
  try {
    const cached = localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        // Invalidate stale cache that was stored without features array
        if (parsed.data && !Array.isArray(parsed.data.features)) {
          return null;
        }
        return parsed.data;
      }
    }
  } catch (e) {
    // ignore
  }
  return null;
};

const setCachedSubscription = (subscription: any): void => {
  try {
    localStorage.setItem(
      SUBSCRIPTION_STORAGE_KEY,
      JSON.stringify({
        data: subscription,
        timestamp: Date.now(),
      }),
    );
  } catch (e) {
    // ignore
  }
};

export const clearSubscriptionCache = (): void => {
  try {
    localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const [subscription, setSubscription] = useState<any | null>(getCachedSubscription());
  const [loading, setLoading] = useState(false);

  // Get auth state from Redux - this is safe because Redux is already loaded
  const isAuthenticated = useAppSelector((state) => state.auth?.isAuthenticated);

  const refreshSubscription = useCallback(async () => {
    // Only fetch if user is logged in
    if (!isAuthenticated) {
      return;
    }

    // SECURITY: Only fetch subscription if user has BILLING_MANAGEMENT privilege
    const hasBillingAccess = hasPrivilege(APP_FEATURES.BILLING_MANAGEMENT);
    if (!hasBillingAccess) {
      setSubscription(null);
      return;
    }

    setLoading(true);
    try {
      // Dynamic import to avoid loading service before auth is ready
      const { fetchCurrentSubscription, fetchMySubscriptionFeatures } = await import(
        '../services/subscription.service'
      );
      const [subResponse, featuresResponse] = await Promise.all([
        fetchCurrentSubscription(),
        fetchMySubscriptionFeatures().catch(() => null),
      ]);
      const sub = (subResponse as any)?.data || subResponse;
      const featuresData = (featuresResponse as any)?.data;
      // Attach flat features array directly onto subscription object
      const subWithFeatures = {
        ...sub,
        features: featuresData?.features || [],
      };
      setSubscription(subWithFeatures);
      setCachedSubscription(subWithFeatures);
    } catch (err: any) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Clear subscription when user logs out
    if (!isAuthenticated) {
      setSubscription(null);
      clearSubscriptionCache();
      return;
    }

    let isMounted = true;
    let privilegesCheckTimer: ReturnType<typeof setTimeout> | null = null;
    let isRefreshing = false; // Prevent duplicate calls

    // On login, wait for privileges to be loaded before checking access
    const handlePrivilegesLoaded = () => {
      if (!isMounted || isRefreshing) return;

      // Clear timeout to prevent race condition
      if (privilegesCheckTimer) {
        clearTimeout(privilegesCheckTimer);
        privilegesCheckTimer = null;
      }

      const hasBillingAccess = hasPrivilege(APP_FEATURES.BILLING_MANAGEMENT);

      if (!hasBillingAccess) {
        // User authenticated but no billing access
        setSubscription(null);
        clearSubscriptionCache();
        return;
      }

      // Fetch subscription if user has access and no cached data
      if (!subscription) {
        isRefreshing = true;
        refreshSubscription().finally(() => {
          isRefreshing = false;
        });
      }
    };

    // Check immediately (privileges may already be loaded)
    const hasBillingAccess = hasPrivilege(APP_FEATURES.BILLING_MANAGEMENT);

    if (hasBillingAccess && !subscription) {
      isRefreshing = true;
      refreshSubscription().finally(() => {
        isRefreshing = false;
      });
    } else if (!hasBillingAccess) {
      // Privileges not loaded yet, wait for event (with single listener)
      window.addEventListener('privilegesUpdated', handlePrivilegesLoaded, { once: true });

      // Fallback timeout in case event doesn't fire
      privilegesCheckTimer = setTimeout(handlePrivilegesLoaded, 100);
    }

    return () => {
      isMounted = false;
      window.removeEventListener('privilegesUpdated', handlePrivilegesLoaded);
      if (privilegesCheckTimer) clearTimeout(privilegesCheckTimer);
    };
    // Only depend on isAuthenticated - subscription check happens inside
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const hasFeature = useCallback(
    (featureCode: string): boolean => {
      if (!subscription) return true; // Not loaded — don't block (let backend handle)
      // Pure trial (no plan assigned) → full access to everything
      // Matches backend: subscription.is_trial && !subscription.plan_id → true
      const isTrial = (subscription.is_trial || subscription.isTrial) && !subscription.plan_id;
      if (isTrial) return true;
      // Expired or cancelled subscription — block all features except dashboard and billing
      const subscriptionStatus = (subscription.status || '').toLowerCase();
      if (subscriptionStatus === 'expired' || subscriptionStatus === 'cancelled') {
        return false;
      }
      // Use flat features array (fetched via /me/features and attached by refreshSubscription)
      const features: any[] = subscription.features || subscription.plan?.plan_features || [];
      // No feature data at all — conservative: allow through (backend guards)
      if (!features.length) return true;
      const feature = features.find(
        (f: any) => f.feature_code === featureCode || f.code === featureCode,
      );
      // Feature not in plan → plan does NOT include this module → BLOCK
      if (!feature) return false;
      const value = feature.value;
      return Boolean(value) && value !== 'false' && value !== '0' && value !== 'none';
    },
    [subscription],
  );

  const getFeatureValue = useCallback(
    (featureCode: string): string | null => {
      if (!subscription) return null;
      const features: any[] = subscription.features || subscription.plan?.plan_features || [];
      const feature = features.find(
        (f: any) => f.feature_code === featureCode || f.code === featureCode,
      );
      return feature?.value || null;
    },
    [subscription],
  );

  return (
    <SubscriptionContext.Provider
      value={{ subscription, loading, refreshSubscription, hasFeature, getFeatureValue }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

const useFeature = (featureCode: string) => {
  const { subscription, loading, hasFeature, getFeatureValue } = useSubscription();
  const hasAccess = hasFeature(featureCode);
  const isTrial = subscription?.is_trial || subscription?.isTrial || false;
  const value = getFeatureValue(featureCode);
  return { hasAccess, value, loading, isTrial };
};

export const useFeatures = (
  featureCodes?: (FeatureCode | string)[],
): {
  features: Record<string, any>;
  loading: boolean;
} => {
  const { subscription, loading } = useSubscription();

  const features = useMemo(() => {
    const output: Record<string, any> = {};

    const allFeatures: any[] = subscription?.features || subscription?.plan?.plan_features || [];

    if (!subscription || !allFeatures.length) {
      return output;
    }

    // Pure trial (no plan assigned) → full access, matches BE logic
    const isTrial = (subscription.is_trial || subscription.isTrial) && !subscription.plan_id;
    if (isTrial) {
      for (const pf of allFeatures) {
        output[pf.feature_code] = pf.value === 'false' ? false : pf.value || true;
      }
      return output;
    }

    if (featureCodes && featureCodes.length > 0) {
      for (const code of featureCodes) {
        const planFeature = allFeatures.find(
          (f: any) => f.feature_code === code || f.code === code,
        );
        if (planFeature) {
          const value = planFeature.value;
          output[code] = value === 'false' ? false : value === 'true' ? true : value;
        } else {
          output[code] = false; // Feature not in plan → BLOCK (matches hasFeature() behaviour)
        }
      }
    } else {
      for (const pf of allFeatures) {
        const value = pf.value;
        output[pf.feature_code] = value === 'false' ? false : value === 'true' ? true : value;
      }
    }

    return output;
  }, [subscription, featureCodes]);

  return { features, loading };
};

export default useFeature;
